(() => {
  'use strict';

  const popups = document.querySelectorAll('[data-video-popup]');
  if (!popups.length) return;

  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STORAGE_PREFIX = 't4-video-popup-';
  const CLOSE_ANIMATION_MS = 320;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const DESIGN_MODE = Boolean(window.Shopify && window.Shopify.designMode);

  const readStoredTime = (key) => {
    try {
      const value = window.localStorage.getItem(key);
      return value ? parseInt(value, 10) || 0 : 0;
    } catch (error) {
      return 0;
    }
  };

  const storeTime = (key) => {
    try {
      window.localStorage.setItem(key, String(Date.now()));
    } catch (error) {
      // Private browsing can block storage. The popup still works.
    }
  };

  const isDismissed = (frequency, key) => {
    if (frequency === 'always') return false;
    const saved = readStoredTime(key);
    if (!saved) return false;
    if (frequency === 'once') return true;
    const limit = frequency === 'week' ? DAY_MS * 7 : DAY_MS;
    return Date.now() - saved < limit;
  };

  popups.forEach((root) => {
    const sectionId = root.dataset.sectionId || 'default';
    const delay = Math.max(0, parseInt(root.dataset.delay, 10) || 0) * 1000;
    const frequency = root.dataset.frequency || 'always';
    const storageKey = STORAGE_PREFIX + sectionId;

    const backdrop = root.querySelector('[data-popup-backdrop]');
    const closeButton = root.querySelector('[data-popup-close]');
    const muteButton = root.querySelector('[data-popup-mute]');
    const playButton = root.querySelector('[data-popup-play]');
    const tabButton = root.parentElement
      ? root.parentElement.querySelector('[data-popup-tab]')
      : null;
    const frames = Array.from(root.querySelectorAll('[data-media]'));

    let isOpen = false;
    let lastFocused = null;
    let closeTimer = null;

    const getActiveFrame = () => {
      const wanted = MOBILE_QUERY.matches ? 'mobile' : 'desktop';
      return root.querySelector('[data-media="' + wanted + '"]') || frames[0] || null;
    };

    const getVideo = () => {
      const frame = getActiveFrame();
      return frame ? frame.querySelector('video') : null;
    };

    const pauseAll = () => {
      frames.forEach((frame) => {
        const video = frame.querySelector('video');
        if (video) video.pause();
      });
    };

    const syncFrames = () => {
      const active = getActiveFrame();
      frames.forEach((frame) => {
        frame.hidden = frame !== active;
      });
    };

    const playActive = () => {
      const video = getVideo();
      if (!video) return;
      video.muted = true;
      if (muteButton) {
        muteButton.dataset.muted = 'true';
        muteButton.setAttribute('aria-label', 'Turn sound on');
      }
      if (playButton) playButton.hidden = true;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          if (playButton) playButton.hidden = false;
        });
      }
    };

    const openPopup = () => {
      if (isOpen) return;
      if (!getVideo()) return;
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocused = document.activeElement;
      syncFrames();
      if (tabButton) tabButton.hidden = true;
      root.hidden = false;
      document.body.classList.add('t4-video-popup-open');
      window.requestAnimationFrame(() => root.classList.add('is-open'));
      isOpen = true;
      playActive();
      if (closeButton) closeButton.focus({ preventScroll: true });
    };

    const closePopup = (remember) => {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      pauseAll();
      document.body.classList.remove('t4-video-popup-open');
      if (tabButton) tabButton.hidden = false;
      if (remember !== false && frequency !== 'always') storeTime(storageKey);
      const finish = () => {
        root.hidden = true;
        closeTimer = null;
      };
      if (REDUCED_MOTION.matches) {
        finish();
      } else {
        closeTimer = window.setTimeout(finish, CLOSE_ANIMATION_MS);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    };

    const getFocusables = () => {
      return Array.from(root.querySelectorAll('button, [href]')).filter((element) => {
        return !element.hidden && element.getClientRects().length > 0;
      });
    };

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = getFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (backdrop) {
      backdrop.addEventListener('click', () => closePopup());
    }

    if (tabButton) {
      tabButton.addEventListener('click', () => openPopup());
    }

    if (closeButton) {
      closeButton.addEventListener('click', () => closePopup());
    }

    // Count the "once" whenever the visitor leaves while the popup is open -
    // refresh, back button, any link, or the CTA navigation. Without this, the
    // popup reappears on the next page because dismissal was never recorded.
    const ctaLink = root.querySelector('.t4-video-popup__cta[href]');
    if (ctaLink) {
      ctaLink.addEventListener('click', () => {
        if (frequency !== 'always') storeTime(storageKey);
      });
    }

    window.addEventListener('pagehide', () => {
      if (isOpen && frequency !== 'always') storeTime(storageKey);
    });

    if (playButton) {
      playButton.addEventListener('click', () => {
        const video = getVideo();
        if (!video) return;
        video.muted = true;
        playButton.hidden = true;
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(() => {
            playButton.hidden = false;
          });
        }
      });
    }

    if (muteButton) {
      muteButton.addEventListener('click', () => {
        const video = getVideo();
        if (!video) return;
        video.muted = !video.muted;
        const muted = video.muted;
        muteButton.dataset.muted = muted ? 'true' : 'false';
        muteButton.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
      });
    }

    const handleQueryChange = () => {
      if (!isOpen) {
        syncFrames();
        return;
      }
      pauseAll();
      syncFrames();
      playActive();
    };

    if (typeof MOBILE_QUERY.addEventListener === 'function') {
      MOBILE_QUERY.addEventListener('change', handleQueryChange);
    } else if (typeof MOBILE_QUERY.addListener === 'function') {
      MOBILE_QUERY.addListener(handleQueryChange);
    }

    syncFrames();

    const dismissed = !DESIGN_MODE && isDismissed(frequency, storageKey);

    if (dismissed) {
      if (tabButton) tabButton.hidden = false;
    } else {
      window.setTimeout(openPopup, DESIGN_MODE ? 0 : delay);
    }
  });
})();
